/* External */
import {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

/* Components */
import PlayerMessage from '~/components/Player/PlayerMessage';

/* Hooks */
import useQuery from '~/hooks/useQuery';
import useChannel from '~/hooks/useChannel';
import useContent from '~/hooks/useContent';
import useFetchVodContentProgress from '~/hooks/useFetchVodContentProgress';
import useLegoTrackPreference from '~/hooks/legoTrackPreference';
import useLegoMiniEpg from '~/hooks/legoMiniEpg';
import useNextEpisode from '~/hooks/useNextEpisode';
import useRedirectToPlayer from '~/hooks/useRedirectToPlayer';
import usePlaybackHeartbeatController from '~/components/Player/VrioPlayer/utils/usePlaybackHeartbeatController';

/* Utils */
import { getImage } from '~/utils/imageUtils';
import { getTitle, isEpisode } from '~/utils/metaDataUtils';
import { ImageRatio } from '~/utils/constants';
import { PLAY_SOURCE_PAGE, PLAY_SOURCE_SECTION } from '~/utils/trackSourceConviva';
import {
  getPlayerLoaderBadgeLabel,
  getPlayerLoaderSecondSubtitleParts,
  getPlayerLoaderSubtitle,
} from '~/components/Player/PlayerUI/utils';

/* Storage */
import { fetchContentById } from '~/storage/contents';
import { fetchAllChannels } from '~/storage/channels/actions';
import { selectIsFetchingChannels } from '~/storage/status';

/* Providers */
import {
  useConfig, useCountry, useIsBrazil, useIsFetchingConfig,
} from '~/providers/ConfigProvider';
import {
  useConnectionStatus,
  useOnDisconnectedMessageHide,
  useSetShowDisconnectedMessage,
} from '~/providers/ConnectionStatusProvider';
import { useOnBackground } from '~/providers/ForegroundAppProvider';
import { useDeviceInfo } from '~/providers/DeviceInfoProvider';
import { useUserAccount } from '~/providers/UserProvider';

/* Services */
import backendService from '~/services/backendService';
import { checkToken } from '~/services/authService';

/* Utils */
import { Errors, Tracks } from '@dtvgo/player-utils';
import { useGetSync } from '@dtvgo/react-utils';
import { getMSOProviderFormatted } from '@dtvgo/utils';
import platform from '~/utils/platform';
import { goBack, redirectTo } from '~/utils/history';
import { getLink, getPath, Routes } from '~/utils/router';
import {
  APP_VERSION,
  IS_LEGO_PLAYER_LOADING_ANIMATION_ACTIVE,
  IS_LEGO_PLAYER_MINI_EPG_ACTIVE,
  IS_LEGO_PLAYER_SUBTITLES_ACTIVE,
  IS_PLAYER_DIAGNOSTICS_ACTIVE,
  isProgramCompletionActive,
  legoPlayerConfigOverride,
  lowLatencyConfigOverride,
  PLAYER_FETCH_SCHEDULES_RETRY_COUNT,
  PLAYER_FETCH_SCHEDULES_RETRY_SECONDS,
  USE_LOW_LATENCY_STREAMS,
} from '~/utils/env';
import isExtendedSeekEnabled from '~/utils/extendedSeekGate';
import { getPlayerConfigOverrideByOS } from '~/utils/profiles';
import { isLowEndDeviceLego as detectLowEndDevice } from '~/utils/platform/isLowEndDevice';
import { initLegoFirebasePerf } from '~/utils/firebasePerf';

/* Same localStorage-overridden LL override as ShakaPlayer.jsx getLowLatencyConfigOverride. */
function getLowLatencyConfigOverride() {
  try {
    return JSON.parse(localStorage.getItem('lowLatencyConfigOverride')) || lowLatencyConfigOverride;
  } catch {
    return lowLatencyConfigOverride;
  }
}

/* The up-next program (the "A SEGUIR" card): the first schedule that starts
   after "now" (the now-next payload carries the following one). Schedules
   without a parsable startTime produce NaN comparisons and are skipped. */
function pickNextProgram(schedules) {
  const nowMs = Date.now();
  return schedules.find((candidate) => {
    const startTs = new Date(candidate.startTime ?? candidate.live?.startTime).getTime();
    return Number.isFinite(startTs) && startTs > nowMs;
  });
}

/* iso-language-converter carries a ~70 KB ISO-639 JSON table that is parsed
   when the module evaluates. This route is imported statically by Private.jsx,
   so a static import put that parse on every app boot; it is loaded next to
   the player chunk instead (see loadPlayerModules) — track labels are only
   read after the player mounts. */
let isoLanguageConverter;

async function loadIsoLanguageConverter() {
  const { default: converter } = await import('iso-language-converter/index.es5');
  isoLanguageConverter = converter;
}

async function loadPlayerModules() {
  const [playerModule] = await Promise.all([
    import('~/lego-player/player/mountPlayer'),
    loadIsoLanguageConverter(),
  ]);
  return playerModule;
}

/* Resolve a translatable ISO language from the generic track, matching the
   legacy shaka-player-react + Tracks.parseAudioTracks path:
   - qad/qae/qaf are descriptive-audio pseudo-languages
   - 2/3-letter ISO codes go through iso-language-converter (por -> pt)
   - internal labels like PT$1/EN$1 are only a fallback. */
function resolveTrackLanguage(track) {
  const rawLanguage = String(track?.language ?? '').toLowerCase().split('$')[0];
  const descriptive = Tracks.DescriptiveAudio[rawLanguage];
  if (descriptive) {
    return { iso: descriptive.iso, isDescriptive: true };
  }

  const labelCode = String(track?.label ?? '')
    .trim()
    .toLowerCase()
    .match(/^([a-z]{2,3})\$\d+$/)?.[1] ?? '';

  let iso = '';
  for (const candidate of [rawLanguage, labelCode].filter(Boolean)) {
    const exact = isoLanguageConverter?.(candidate, { to: 1 });
    const base = candidate.split('-')[0];
    const baseIso = isoLanguageConverter?.(base, { to: 1 });
    if (exact) {
      iso = exact;
      break;
    }
    if (baseIso) {
      iso = baseIso;
      break;
    }
  }

  if (!iso) {
    const base = rawLanguage.split('-')[0];
    if (Tracks.KNOWN_LANGUAGES.has(base)) iso = base;
  }

  return { iso, isDescriptive: false };
}

/* Build the LegoProgramWindow object from the current now-next schedule. Keep
   the start/end epoch + the NPAW content metadata + the up-next entry. */
function createProgramWindow({
  schedule,
  channelName,
  nextSchedule,
  contentIdValue,
}) {
  if (!schedule) return undefined;
  const startTimeValue = schedule.startTime ?? schedule.live?.startTime;
  const endTimeValue = schedule.endTime ?? schedule.live?.endTime;
  if (startTimeValue === undefined || endTimeValue === undefined) return undefined;
  return {
    startEpoch: new Date(startTimeValue).getTime() / 1000,
    endEpoch: new Date(endTimeValue).getTime() / 1000,
    /* NPAW content metadata — the PROGRAM title. Confirmed against the real
       now-next payload (2026-09-01): the schedule's OWN `.title` is the program
       title (e.g. TNT -> "Monster Hunter"); there is NO `.content` sub-object in
       this endpoint's response (my earlier content?.title read was wrong), and
       the channel name lives at `.live.channelName`, NOT the schedule title. */
    title: schedule.title ?? schedule.live?.title,
    channelName: schedule.live?.channelName
      ?? schedule.channelName
      ?? channelName
      ?? contentIdValue,
    next: nextSchedule
      ? {
        title: nextSchedule.title ?? nextSchedule.live?.title,
        startTime: nextSchedule.startTime ?? nextSchedule.live?.startTime,
        channelName: nextSchedule.live?.channelName
          ?? nextSchedule.channelName
          ?? channelName,
      }
      : undefined,
  };
}

/* Loading-screen metadata — a port of the old PlayerUI LoaderScreenV2. Every
   field is display-ready: the strings are resolved HERE with the legacy
   loader's own helpers + react-intl, because the Solid tree renders only and
   never imports app modules or i18n. Live uses the raw now-next schedule (its
   content fields + `live`), VOD the content entity — exactly the pair the old
   Live/Vod routes handed LoaderScreen. */
const LOADER_IMAGE_WIDTH = 1920;
const LOADER_IMAGE_RATIO = ImageRatio.SIXTEEN_BY_NINE;

function createLoaderMeta({
  content, schedule, formatMessage, requestCompleted,
}) {
  /* The second line is a rating badge + a text tail; the badge element cannot
     cross the Solid boundary, so the parts travel separately. */
  const { rating, text } = getPlayerLoaderSecondSubtitleParts({ content, schedule });

  return {
    title: getTitle(content, schedule, { formatMessage }),
    subtitlePrimary: getPlayerLoaderSubtitle(content, formatMessage),
    subtitleSecondary: text,
    rating,
    badge: getPlayerLoaderBadgeLabel(content, formatMessage),
    backdropUrl: getImage(content?.images, LOADER_IMAGE_WIDTH, LOADER_IMAGE_RATIO) || '',
    requestCompleted: !!requestCompleted,
    loadingText: formatMessage({ id: 'player.loading' }),
    missingMetadataText: formatMessage({ id: 'schedule.missing.metadata' }),
  };
}

/* Labels-only loading meta, used to seed getLoader so the loading screen shows
   its copy (and the dark scrim) from the FIRST frame — the full meta only lands
   with the now-next / content fetch, and the old loader always showed
   "player.loading" immediately. */
function getLoaderLabels(formatMessage) {
  return {
    requestCompleted: false,
    loadingText: formatMessage({ id: 'player.loading' }),
    missingMetadataText: formatMessage({ id: 'schedule.missing.metadata' }),
  };
}

/* Next-episode wiring for the VOD "Próximo episódio" center button (series
   only). Returns STABLE ref-backed getters (useMemo) so the solid mount
   identity stays constant while the next-episode fetch settles; live always
   returns "no next episode". Mirrors the old player's Vod route
   (useNextEpisode + useRedirectToPlayer). */
function useLegoNextEpisode(content, isLive) {
  const nextEpisode = useNextEpisode(content);
  const goToNextEpisode = useRedirectToPlayer(nextEpisode, {
    page: PLAY_SOURCE_PAGE.PLAYER,
    section: PLAY_SOURCE_SECTION.VOD_NEXT_EPISODE,
    replace: true,
    state: {
      actionOrigin: content?.get('actionOrigin'),
    },
  });

  const nextEpisodeRef = useRef(undefined);
  nextEpisodeRef.current = isLive ? undefined : nextEpisode?.toJS?.();
  const goToNextEpisodeRef = useRef(() => {});
  goToNextEpisodeRef.current = isLive ? () => {} : goToNextEpisode;

  /* useMemo keeps the function identities stable across renders so the mount
     effect's dependency array doesn't remount the player every render. */
  return useMemo(() => ({
    getNextEpisode: () => nextEpisodeRef.current,
    onNextEpisode: () => goToNextEpisodeRef.current(),
  }), []);
}

/* The live program as the heartbeat reads it (the classic VrioPlayer's
   getAssetAndScheduleForHeartbeat over the now-next schedule). */
function getLiveHeartbeatContent(schedule) {
  if (!schedule) return { asset: undefined, schedule: undefined };
  const startTime = schedule.live?.startTime ?? schedule.startTime;
  const endTime = schedule.live?.endTime ?? schedule.endTime;
  return {
    asset: {
      vmsId: schedule.vmsId,
      vrioAssetId: schedule.vrioAssetId,
      duration: schedule.duration,
    },
    schedule: {
      guideId: schedule.guideId,
      startTime: startTime && new Date(startTime),
      endTime: endTime && new Date(endTime),
    },
  };
}

/* Playback session, mirroring the classic VrioPlayer + Live/Vod routes:
   - the PlaybackHeartbeatController (Continue Watching progress, streamed
     time) starts after each authorizer answer, is fed the playhead/duration
     (VOD) or the timeshift (live) and stops on the end, when the player
     unmounts or remounts, and before a new start;
   - live follows the program on air (onLiveSchedule, from the shell's now-next
     fetch): a new program resets the streamed counters and becomes the
     heartbeat content, and with the program-completion flag the one that
     ended is reported first;
   - the end of content (VOD) goes to the next episode, else back (the detail
     page).
   Returns a STABLE object (refs inside), so it can sit in the mount effect
   without remounting the player. */
function useLegoPlaybackSession({
  contentRef, contentId, isLive, savedProgress, getNextEpisode, onNextEpisode, homeOnBack,
}) {
  const {
    playbackHeartbeatController,
    streamedTimeCounter,
    streamedContentTimeCounter,
  } = usePlaybackHeartbeatController();
  const stateRef = useRef({});
  stateRef.current = {
    contentId, isLive, getNextEpisode, onNextEpisode, homeOnBack,
  };
  const liveScheduleRef = useRef(undefined);
  const isPlayingRef = useRef(false);

  const startCounters = () => {
    streamedTimeCounter.start();
    streamedContentTimeCounter.start();
  };
  const stopCounters = () => {
    streamedTimeCounter.stop();
    streamedContentTimeCounter.stop();
  };
  const stopPlayback = useRef(async () => {});
  stopPlayback.current = async () => {
    isPlayingRef.current = false;
    stopCounters();
    await playbackHeartbeatController.stop();
    streamedTimeCounter.reset();
    streamedContentTimeCounter.reset();
  };

  /* The classic seeds the heartbeat with the saved progress as soon as it is
     known, so a stop before the first timeupdate does not report 0. */
  useEffect(() => {
    if (!isLive && savedProgress !== undefined) {
      playbackHeartbeatController.updateProgress(savedProgress);
    }
  }, [isLive, playbackHeartbeatController, savedProgress]);

  /* A new channel starts without the previous one's program. */
  useEffect(() => {
    liveScheduleRef.current = undefined;
  }, [contentId]);

  useEffect(() => () => {
    stopPlayback.current();
  }, []);

  return useMemo(() => ({
    onAuthorized: async () => {
      const { current } = stateRef;
      await stopPlayback.current();
      if (current.isLive) {
        playbackHeartbeatController.start({
          playbackId: current.contentId,
          isLive: true,
          ...getLiveHeartbeatContent(liveScheduleRef.current),
        });
        return;
      }
      const content = contentRef.current;
      playbackHeartbeatController.start({
        playbackId: current.contentId,
        isLive: false,
        asset: {
          vmsId: content?.vmsId,
          vrioAssetId: content?.vrioAssetId,
          duration: content?.duration,
        },
        schedule: { guideId: content?.guideId, startTime: undefined, endTime: undefined },
      });
    },
    onLiveSchedule: (schedule) => {
      const previous = liveScheduleRef.current;
      liveScheduleRef.current = schedule;
      if (!schedule || schedule.guideId === previous?.guideId) return;
      /* Reported while the finished program is still the heartbeat content
         (the controller sends nothing before a playback started). */
      const previousEnd = previous && new Date(previous.live?.endTime ?? previous.endTime);
      if (isProgramCompletionActive && previousEnd && Date.now() >= previousEnd.getTime()) {
        playbackHeartbeatController.programCompletion();
      }
      stopCounters();
      streamedTimeCounter.reset();
      streamedContentTimeCounter.reset();
      playbackHeartbeatController.setContent({
        isLive: true,
        ...getLiveHeartbeatContent(schedule),
      });
      if (isPlayingRef.current) startCounters();
    },
    onStateChange: (state) => {
      switch (state) {
        case 'PLAYING': {
          isPlayingRef.current = true;
          startCounters();
          break;
        }
        case 'PAUSED': {
          isPlayingRef.current = false;
          stopCounters();
          break;
        }
        case 'BUFFERING': {
          stopCounters();
          break;
        }
        default: {
          break;
        }
      }
    },
    onProgress: (position, duration) => {
      if (stateRef.current.isLive) return;
      if (Number.isFinite(position)) playbackHeartbeatController.updateProgress(position);
      if (Number.isFinite(duration) && duration > 0) {
        playbackHeartbeatController.updateDuration(duration);
      }
    },
    onLiveTimeShift: (timeShift) => {
      if (stateRef.current.isLive) playbackHeartbeatController.updateProgress(timeShift);
    },
    onEnded: () => {
      const { current } = stateRef;
      if (current.isLive) return;
      stopPlayback.current();
      if (current.getNextEpisode()) {
        current.onNextEpisode();
      } else {
        exitLegoPlayer(current.homeOnBack);
      }
    },
    /* The player unmounted (error screen, remount): no heartbeat without it. */
    stop: () => stopPlayback.current(),
  // eslint-disable-next-line react-hooks/exhaustive-deps -- refs + stable controller/counters
  }), []);
}

/* The classic routes give analytics a moment to report an authorizer failure
   before the error screen replaces the player (useHandlePlayerError's
   reportable path); engine errors are already reported when they surface. */
const REPORTABLE_ERROR_DELAY_MS = 1000;

/* The lego player's fatal-error handling, mirroring the classic Live/Vod routes
   (useHandlePlayerErrorWithReloadOnConnected + PlayerMessage). Automatic
   recovery (expired license token, a one-off technical failure) happens INSIDE
   the lego, in place and at the user's position; what reaches this hook is
   final for the mount:
   - a parental-control block goes to the player error page;
   - anything else renders the app's error screen, with a retry button for
     TECHNICAL errors (a BUSINESS one — entitlement, region, concurrency — would
     only fail again);
   - when the failure was a lost connection, the error clears (and the player
     remounts) once the connection is back.
   Unlike the shared hook it can fail again after a retry — that hook latches
   after its first error. A retry/remount continues VOD from where it failed
   (`getRetryResumeTime`), not from the progress saved when the page opened. */
function useLegoFatalError({ content, isLive, contentId }) {
  const history = useHistory();
  const [playerError, setPlayerError] = useState(undefined);
  const retryResumeTimeRef = useRef(undefined);
  const displayTimeoutRef = useRef(undefined);
  const getPlayerError = useGetSync(playerError);
  const isConnected = useGetSync(useConnectionStatus().isConnected);
  const setShowDisconnectedMessage = useSetShowDisconnectedMessage();

  const handleRef = useRef(() => {});
  handleRef.current = ({ error, authorizerError, resumeTime }) => {
    const parsed = authorizerError
      ? Errors.parseAuthorizerError(authorizerError)
      : Errors.parsePlayerError(error);
    if (parsed?.category === Errors.ErrorCategory.PARENTAL) {
      history.replace(getLink(Routes.Private.Player.Error), {
        parentalControl: true,
        content: isLive ? undefined : content,
      });
      return;
    }
    retryResumeTimeRef.current = resumeTime;
    clearTimeout(displayTimeoutRef.current);
    displayTimeoutRef.current = setTimeout(
      () => setPlayerError(parsed),
      authorizerError ? REPORTABLE_ERROR_DELAY_MS : 0,
    );
    /* Same as the classic hook: a failure while offline shows the disconnected
       message right away (skipping its debounce). */
    if (!isConnected()) setShowDisconnectedMessage(true);
  };
  const onFatalError = useMemo(() => (failure) => handleRef.current(failure), []);
  const getRetryResumeTime = useMemo(() => () => retryResumeTimeRef.current, []);

  /* Clearing the error remounts the player (see the mount effect). */
  const retry = useMemo(() => () => setPlayerError(undefined), []);
  const canRetry = playerError?.group === Errors.ErrorGroup.TECHNICAL;

  /* Connection back: clear the error, which remounts the player. */
  useOnDisconnectedMessageHide(() => {
    if (getPlayerError()) setPlayerError(undefined);
  });

  /* A different content starts from its own saved progress. */
  useEffect(() => {
    retryResumeTimeRef.current = undefined;
  }, [contentId]);

  useEffect(() => () => clearTimeout(displayTimeoutRef.current), []);

  return {
    playerError,
    onFatalError,
    getRetryResumeTime,
    retry: canRetry ? retry : undefined,
  };
}

/* Leaving the player, like the classic Live route's exitPlayer. */
function exitLegoPlayer(homeOnBack) {
  if (homeOnBack) {
    redirectTo(getPath(Routes.Private));
  } else {
    goBack();
  }
}

/* The classic player's error screen (the same component and messages as the
   Live/Vod routes); BACK / the button leave the player like the classic exit. */
function LegoErrorScreen({
  error, content, channel, isLive, channelId, homeOnBack, onRetry,
}) {
  const exitPlayer = () => exitLegoPlayer(homeOnBack);
  const contentTitle = isLive
    ? channel?.title
    : content?.episode?.showName || content?.title;
  return (
    <PlayerMessage
      content={isLive ? undefined : content}
      error={error}
      contentTitle={contentTitle || ''}
      channelId={isLive ? channelId : ''}
      exit={exitPlayer}
      onRetry={onRetry}
    />
  );
}

LegoErrorScreen.propTypes = {
  error: PropTypes.shape({ category: PropTypes.string }).isRequired,
  content: PropTypes.shape({
    title: PropTypes.string,
    episode: PropTypes.shape({ showName: PropTypes.string }),
  }),
  channel: PropTypes.shape({ title: PropTypes.string }),
  isLive: PropTypes.bool.isRequired,
  channelId: PropTypes.string.isRequired,
  homeOnBack: PropTypes.string,
  onRetry: PropTypes.func,
};

function LegoPlayer() {
  const {
    contentId, isLive, start, origin, homeOnBack,
  } = useQuery();
  const containerRef = useRef(null);
  const [importError, setImportError] = useState('');
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  /* Localized track labels for the solid side panel. These are stable
     ref-backed functions so locale updates never remount the player. */
  const audioTrackLabelRef = useRef((track, index) => (
    track?.label || track?.language || String(index + 1)
  ));
  const textTrackLabelRef = useRef((track, index) => (
    track?.label || track?.language || String(index + 1)
  ));
  const trackPanelLabelsRef = useRef({
    audioTitle: 'Idiomas',
    subtitlesTitle: 'Legendas',
    disabled: 'Desativadas',
  });
  audioTrackLabelRef.current = (track, index) => {
    const { iso, isDescriptive } = resolveTrackLanguage(track);
    if (!iso || !Tracks.KNOWN_LANGUAGES.has(iso)) {
      return formatMessage({ id: 'tracks.audio.unknown' }, { index: index + 1 });
    }
    const translationId = isDescriptive ? `tracks.${iso}.descriptive` : `tracks.${iso}`;
    const baseLabel = formatMessage({ id: translationId });
    /* The old Tracks.parseAudioTracks did an exact Set.has() on the plain codec,
       which only matches when shaka reports a bare 'ec-3'/'ac-3'. The adapter
       forwards shaka's audio `codecs`, which can be a codec list / MIME-ish
       string ("ec-3,mp4a.40.2", 'audio/mp4; codecs="ec-3"'), so tokenize before
       matching — the exact check silently dropped the Dolby suffix there. */
    const codecTokens = String(track?.codecs ?? '')
      .toLowerCase()
      .split(/[^\da-z-]+/)
      .filter(Boolean);
    const isDolby = codecTokens.some(
      (token) => Tracks.DOLBY_DIGITAL_REPRESENTATION_CODECS.has(token),
    );
    return isDolby
      ? `${baseLabel} ${formatMessage({ id: 'tracks.dolbyDigital' })}`
      : baseLabel;
  };
  textTrackLabelRef.current = (track, index) => {
    const { iso } = resolveTrackLanguage(track);
    if (!iso || !Tracks.KNOWN_LANGUAGES.has(iso)) {
      return formatMessage({ id: 'tracks.text.unknown' }, { index: index + 1 });
    }
    const baseLabel = formatMessage({ id: `tracks.${iso}` });
    const kind = String(track?.kind ?? '').toLowerCase();
    const isClosedCaption = kind === 'caption' || kind === 'captions';
    return isClosedCaption ? `${baseLabel}${Tracks.CLOSED_CAPTIONS}` : baseLabel;
  };
  trackPanelLabelsRef.current = {
    audioTitle: formatMessage({ id: 'common.audioTrack' }),
    subtitlesTitle: formatMessage({ id: 'common.subtitles' }),
    disabled: formatMessage({ id: 'common.disabled' }),
  };

  /* Audio/subtitle preference persistence — same localStorage entry the old
     player writes (hooks/savedAudioAndSubtitlesHooks.js), so the two players
     share the user's choice. Stable ref-backed accessors: the solid side reads
     the saved value when tracks land and calls back on every selection. */
  const { getTrackPreference, saveTrackPreference } = useLegoTrackPreference();

  const contentIdValue = String(contentId ?? '');
  const isLiveValue = String(isLive ?? '') === 'true';

  /* Mini EPG (guide): channels/schedules/filters stay in react-land (Redux +
     Immutable + react-intl). The hook returns STABLE accessors, so adding it to
     the mount effect below never remounts the player. It fetches nothing until
     the guide button is pressed. */
  const guide = useLegoMiniEpg({
    isLive: isLiveValue,
    currentChannelId: contentIdValue,
    homeOnBack,
  });

  /* Extended DVR ("catch up") — the SAME shared gate the classic player's
     PlayerUI uses, resolved here so the lego keeps a single boolean prop. It is
     NOT a low-latency feature: the time-shifted reload sends the MediaPackage
     `start=` parameter, which normal DASH MPDs honour too (and the bounded
     manifest is itself cheaper on the old TVs). */
  const extendedSeekEnabled = isExtendedSeekEnabled(contentIdValue);

  /* NPAW content metadata. VOD: the content entity is already in Redux when
     navigating from detail pages (the detail page fetched it before
     redirect); deep links with a cold store dispatch the same fetch. Live:
     the metadata comes from the EPG program fetch (programRef) + the channel
     entity below. The hooks are unconditional (rules-of-hooks): for live the
     content lookup misses (channels are not content entities) and vice versa,
     so the isLive gate only decides what is USED. */
  const content = useContent(contentIdValue);
  const channel = useChannel(contentIdValue);
  const {
    playerError, onFatalError, getRetryResumeTime, retry,
  } = useLegoFatalError({
    content,
    isLive: isLiveValue,
    contentId: contentIdValue,
  });

  /* Like the classic Live/Vod routes: leave the player when the app goes to
     the background — a webOS resume with a suspended MSE/DRM pipeline tends to
     fail anyway, and the live position would be stale. */
  useOnBackground(useCallback(() => exitLegoPlayer(homeOnBack), [homeOnBack]));
  const isFetchingChannels = useSelector(selectIsFetchingChannels);
  const contentRef = useRef(undefined);
  contentRef.current = isLiveValue ? undefined : content?.toJS?.() ?? undefined;
  const channelTitleRef = useRef(undefined);
  channelTitleRef.current = isLiveValue ? channel?.title : undefined;
  /* Channel logo URL for the V2 channel-logo pill, resolved like the old
     player (Player.jsx getImage(channel?.images, CHANNEL_LOGO_WIDTH, '4:3')).
     A ref so the mount effect identity is stable while the entity loads. */
  const channelLogoRef = useRef('');
  channelLogoRef.current = isLiveValue
    ? getImage(channel?.images, 180, '4:3') || ''
    : '';
  /* Ref so the mount effect (which reads it via buildNpawOptions) never
     remounts the player on a query change. */
  const originRef = useRef(origin);
  originRef.current = origin;

  /* Loading-screen metadata (see createLoaderMeta). Ref-backed like programRef:
     the Solid tree is not reactive to it, so the player polls getLoader once a
     second and re-renders on a real change. */
  const loaderRef = useRef(getLoaderLabels(formatMessage));
  /* The builder is read through a ref so the live fetch effect (which must NOT
     re-run on a locale change) still resolves labels with the current intl. */
  const buildLoaderMetaRef = useRef(() => {});
  buildLoaderMetaRef.current = (contentValue, scheduleValue, requestCompleted) => createLoaderMeta({
    content: contentValue,
    schedule: scheduleValue,
    formatMessage,
    requestCompleted,
  });

  /* VOD/TV resume: the backend-saved playback position (seconds), computed like
     the old player's Vod route (useFetchVodContentProgress → savedProgress,
     already clamped to 0 for finished / near-the-end playback). Live never
     resumes (0). A ref so the mount identity stays stable while the progress
     fetch settles and the lego reads the LATEST value at load time. */
  const { savedProgress } = useFetchVodContentProgress({
    contentId: contentIdValue,
    duration: content?.duration,
    isEpisode: isEpisode(content),
  });
  const resumePositionRef = useRef(0);
  resumePositionRef.current = isLiveValue ? 0 : (savedProgress || 0);

  const dispatchContentFetchRef = useRef(false);
  useEffect(() => {
    if (isLiveValue || !contentIdValue) {
      dispatchContentFetchRef.current = false;
      return undefined;
    }
    if (!content && !dispatchContentFetchRef.current) {
      dispatchContentFetchRef.current = true;
      const cleanContentId = contentIdValue?.match(/^[\dA-Fa-f-]{36}/)?.[0] ?? contentIdValue;
      dispatch(fetchContentById({ contentId: cleanContentId }));
    }
    return undefined;
  }, [contentIdValue, isLiveValue, content, dispatch]);

  /* VOD loading-screen metadata: rebuild whenever the content entity lands (the
     now-next live branch lives in fetchProgram, where that payload arrives).
     `content` is the reactive selector result, so this re-runs on the fetch
     settling — the lego's poll then picks the new ref up. `requestCompleted` is
     approximated with "the entity resolved": the old route's real request flag
     is not available here (only the channels slice exposes a fetching flag), so
     the "missing metadata" copy only appears for an entity that exists without
     a title. */
  useEffect(() => {
    if (isLiveValue) return;
    loaderRef.current = buildLoaderMetaRef.current(contentRef.current, undefined, !!content);
  }, [content, isLiveValue]);

  /* Live channel name self-heal: NPAW's /start needs content.title
     (LIVE-<channel>) as early as possible — the channel entity lives in the
     channels store, normally populated by Private.jsx's boot fetch. When it's
     missing (e.g. navigated from Home before the boot fetch settled), (re)fill
     the store; the saga retries internally and the 1s refresh tick then sets
     the title, instead of waiting on the (intermittent) now-next fetch.
     ONCE per channel: a channel that is not in the user's list (e.g. a
     regional affiliate the account is not entitled to) never resolves, and an
     ungated refetch re-fired on every settle — measured on webOS 3: 202
     /live/channels calls in 5 min, the main thread ~83% busy. */
  const channelsSelfHealRef = useRef();
  useEffect(() => {
    if (!isLiveValue || !contentIdValue) return undefined;
    if (!channel && !isFetchingChannels && channelsSelfHealRef.current !== contentIdValue) {
      channelsSelfHealRef.current = contentIdValue;
      dispatch(fetchAllChannels());
    }
    return undefined;
  }, [channel, contentIdValue, isLiveValue, isFetchingChannels, dispatch]);

  /* Current EPG program (epoch seconds) for the live bar, fetched directly from
     the schedules endpoint (no Redux store involved). getProgram is a plain
     function over this ref — the solid tree reads it on every render/tick, so
     updates land without remounting the player. */
  const programRef = useRef({});

  const {
    osName, sdkVersion, modelName, version,
  } = useDeviceInfo();
  const deviceInfoRef = useRef({
    osName,
    sdkVersion,
    modelName,
    version,
  });
  deviceInfoRef.current = {
    osName,
    sdkVersion,
    modelName,
    version,
  };

  /* NPAW identity, captured via a ref so provider re-renders (token refresh)
     can never change the mount effect's identity and remount the player. */
  const userAccount = useUserAccount() || {};
  const userAccountRef = useRef(userAccount);
  userAccountRef.current = userAccount;

  const { player: { lowLatencyBufferConfig } = {} } = useConfig();
  const isFetchingConfig = useIsFetchingConfig();

  /* Preferred audio language for the manifest preprocessor — hardcoded from
     the country (the app is SKY BR / DGO LatAm), NOT from the remote
     player-config (invisible in the repo). The lego's shaka config lives in
     src/lego-player/player/legoPlayerConfig.ts. */
  const audioLanguage = useCountry() === 'br' ? 'pt' : 'es';
  /* Brazil flag for the rating-badge color (old player's useIsBrazil). */
  const isBrazil = useIsBrazil();

  /* Next episode (VOD series only): the next episode content entity + a
     redirect that reloads the player on it (the old player's Vod route wires
     useNextEpisode + useRedirectToPlayer the same way). Gated to VOD; live
     never has a next episode (its center button is go-live). Extracted so the
     component's cognitive complexity stays within the eslint limit. */
  const { getNextEpisode, onNextEpisode } = useLegoNextEpisode(content, isLiveValue);

  /* Heartbeat (Continue Watching, live program) + end of content (next
     episode / back). */
  const playbackSession = useLegoPlaybackSession({
    contentRef,
    contentId: contentIdValue,
    isLive: isLiveValue,
    savedProgress,
    getNextEpisode,
    onNextEpisode,
    homeOnBack,
  });

  useEffect(() => {
    if (!isLiveValue || !contentIdValue) {
      programRef.current = {};
      return undefined;
    }

    let cancelled = false;
    let timeout;

    /* Prod refetches when the playhead passes the program end (createHandleLiveProgress
       in @dtvgo/player-utils live.ts). Mirror that: re-check right when the current
       program ends (+2s margin) instead of a fixed interval; 60s fallback when no
       program data (EPG gap/error). now-next like prod's createGetPlayerSchedules. */
    const pickCurrentProgram = (schedules) => {
      const now = Date.now();
      /* Prefer the program containing "now"; if no exact match (timing skew on
         a program boundary, or the now-next payload only carries the follow-on
         program), fall back to the FIRST schedule so the program window (and
         its title/channelName) is never empty — the old Live route's schedule
         is always populated. The bar falls back gracefully either way. */
      const containingNow = schedules.find((schedule) => {
        const startTs = new Date(schedule.startTime ?? schedule.live?.startTime).getTime();
        const endTs = new Date(schedule.endTime ?? schedule.live?.endTime).getTime();
        return startTs <= now && now < endTs;
      });
      return containingNow ?? schedules[0];
    };

    /* The now-next request is known to fail intermittently; prod retries it with
       callWithRetries (RETRY_COUNT + 1 attempts, RETRY_SECONDS apart). Mirror that. */
    const fetchSchedulesWithRetries = async () => {
      for (let attempt = 0; attempt <= PLAYER_FETCH_SCHEDULES_RETRY_COUNT; attempt += 1) {
        if (cancelled) return { schedules: [], channelName: undefined };
        try {
          /* eslint-disable-next-line no-await-in-loop -- sequential retries, same as prod */
          const { data } = await backendService.content.v2.getSchedulesByEventType(contentIdValue, {
            eventType: 'now-next',
            pageSize: 2,
            assetToken: false,
          });
          return {
            schedules: data?.channels?.[0]?.schedules ?? [],
            channelName: data?.channels?.[0]?.name,
          };
        } catch {
          if (attempt < PLAYER_FETCH_SCHEDULES_RETRY_COUNT && !cancelled) {
            /* eslint-disable-next-line no-await-in-loop -- retry backoff, same as prod */
            await new Promise((resolve) => {
              setTimeout(resolve, PLAYER_FETCH_SCHEDULES_RETRY_SECONDS * 1000);
            });
          }
        }
      }
      return { schedules: [], channelName: undefined };
    };

    const fetchProgram = async () => {
      const { schedules, channelName } = await fetchSchedulesWithRetries();
      if (cancelled) return;
      const currentSchedule = pickCurrentProgram(schedules);
      const programWindow = createProgramWindow({
        schedule: currentSchedule,
        channelName,
        nextSchedule: pickNextProgram(schedules),
        contentIdValue,
      });
      if (programWindow) programRef.current = programWindow;
      /* The live heartbeat's program (pickCurrentProgram's fallback included,
         so it matches the bar). */
      playbackSession.onLiveSchedule(currentSchedule);
      /* Loading screen (live): the old Live route handed LoaderScreen the raw
         schedule as `content` and its `.live` block as `schedule`. The raw
         now-next payload keeps the content fields top-level; the normalized
         shape nests them under `.content` — honour both. `requestCompleted`
         is true because the request just settled. */
      if (currentSchedule) {
        loaderRef.current = buildLoaderMetaRef.current(
          currentSchedule.content ?? currentSchedule,
          currentSchedule.live,
          true,
        );
      }
      const program = programRef.current;
      const untilEndMs = program?.endEpoch === undefined
        ? undefined
        : (program.endEpoch + 2) * 1000 - Date.now();
      const nextMs = untilEndMs !== undefined && untilEndMs > 0
        ? Math.max(untilEndMs, 1000)
        : 60_000;
      timeout = setTimeout(fetchProgram, nextMs);
    };

    fetchProgram().catch(() => {});
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [contentIdValue, isLiveValue, playbackSession]);

  /* MediaPackage timeshift: ?start=<POSIX epoch seconds> bounds the manifest. */
  const startRaw = typeof start === 'string' ? start : '';
  const startEpochSeconds = startRaw === '' || !Number.isFinite(Number(startRaw))
    ? undefined
    : Number(startRaw);

  /* Resolved here in react-land so the solid chunk never imports app modules.
     The two flags feed the mount's LL source selection; the config object is
     the generic PlayerConfig shape (adapter + shaka config overrides). */
  const useLowLatencyStreams = USE_LOW_LATENCY_STREAMS;
  /* The shared low-end rule (OS + version, Chromium floor, weak hardware). It
     falls back to the platform module's osName — webOS deviceInfo does not
     report one — and to the UA/hardware floor when a degraded WAM answers with
     an EMPTY deviceInfo, so the low-end tuning (720 cap, stall settings) always
     applies on webOS 3/4. */
  const isLowEndDevice = detectLowEndDevice({ osName, sdkVersion });
  const playerConfig = useMemo(() => ({
    playerConfigOverride: getPlayerConfigOverrideByOS(osName, sdkVersion),
    lowLatencyConfigOverride: getLowLatencyConfigOverride(),
    lowLatencyBufferConfig,
  }), [lowLatencyBufferConfig, osName, sdkVersion]);

  useEffect(() => {
    let dispose;
    let cancelled = false;

    /* NPAW identity, mirroring the real player's NpawMonitoring container:
       the same account code + the same adapter options (error parser) + the
       same user/device metadata. Read here (not at render) so provider
       re-renders can never change the mount identity. */
    const buildNpawOptions = () => {
      const accountCode = import.meta.env.VITE_NPAW_ACCOUNT_CODE;
      if (!accountCode) return undefined;
      const {
        uid: userId,
        accountType,
        msoProvider,
        businessUnit,
        subscriberId,
      } = userAccountRef.current || {};
      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const profileId = Object.keys(profile).length === 0 ? userId : profile?.id;
      const {
        modelName: model,
        sdkVersion: osVersion,
        version: firmwareVersion,
      } = deviceInfoRef.current;
      return {
        accountCode,
        platformName: platform.platformName,
        appName: `${import.meta.env.VITE_NPAW_APP_NAME} ${platform.npaw?.name ?? ''}`.trim(),
        appVersion: APP_VERSION,
        contentId: contentIdValue,
        /* Live/VOD classification from the route — NPAW's /init + /start fire
           before the player can know (player.isLive() is false pre-load). */
        isLive: isLiveValue,
        /* playSource (content.customDimension.1) = the navigation origin,
           like the old player's gtmPlaySource (Live/Vod pass origin). */
        playSource: originRef.current,
        /* VOD content entity (title/genre/rating/type/episode…) for the
           NPAW content block — read from Redux by useContent above. */
        content: contentRef.current,
        userId,
        profileId,
        userType: accountType,
        msoProvider: getMSOProviderFormatted({ msoProvider, businessUnit }),
        subscriberId,
        deviceMetadata: { model, osVersion, firmwareVersion },
      };
    };

    /* The mount waits only for the app-level config (ConfigProvider) that
       feeds lowLatencyBufferConfig — NOT for the remote player-config (the
       lego's shaka config is hardcoded in legoPlayerConfig.ts). While the
       error screen is up there is no player; clearing the error remounts it. */
    if (isFetchingConfig || playerError) return undefined;

    /* Firebase Performance Monitoring lives on the lego side only: its init
       (split chunk, cached after the first player) is started here, alongside
       the player chunk, so the first mount does not wait for both in series.
       When the flag is off / init failed it resolves undefined (no-op) — it
       must never block or break the player. */
    const perfPromise = initLegoFirebasePerf().catch(() => undefined);

    loadPlayerModules()
      .then(async ({ mountLegoPlayer }) => {
        /* Wait for the perf handle so the player's startup trace is real. */
        const perf = await perfPromise;
        if (!cancelled && containerRef.current) {
          dispose = mountLegoPlayer(containerRef.current, {
            contentId: contentIdValue,
            isLive: isLiveValue,
            startEpochSeconds,
            backendService,
            platform,
            playerConfig,
            useLowLatencyStreams,
            isLowEndDevice,
            extendedSeekEnabled,
            /* Live channel name seeded from the Redux channel entity (the
               EPG pages populated the store) — the old Live route used
               channel?.title || schedule?.live.channelName. */
            getProgram: () => ({
              ...programRef.current,
              channelName: channelTitleRef.current || programRef.current?.channelName,
            }),
            /* Channel logo URL for the V2 channel-logo pill (old player:
               getImage(channel?.images, 180, '4:3')). Empty string = brand
               fallback in the lego. */
            getChannelLogo: () => channelLogoRef.current,
            /* Loading-screen metadata (display-ready strings + the backdrop
               URL), resolved above and polled by the player like getProgram. */
            getLoader: () => loaderRef.current,
            getContent: () => contentRef.current,
            /* A retry after a failure continues where it failed. */
            getResumePosition: () => getRetryResumeTime() ?? resumePositionRef.current,
            getNextEpisode,
            onNextEpisode,
            npaw: buildNpawOptions(),
            perf,
            /* Env-gated: the focusable "Stats" button (toggles the perf overlay)
               only appears when player diagnostics are enabled — off in prod. */
            showStatsButton: IS_PLAYER_DIAGNOSTICS_ACTIVE,
            /* Env-gated player-chrome feature flags (all default OFF). The
               loading animation used to follow `!isLowEndDevice`; it is now its
               own flag so the tier heuristic no longer decides it. */
            loadingAnimationActive: IS_LEGO_PLAYER_LOADING_ANIMATION_ACTIVE,
            showMiniEpgButton: IS_LEGO_PLAYER_MINI_EPG_ACTIVE,
            showSubtitlesButton: IS_LEGO_PLAYER_SUBTITLES_ACTIVE,
            getDeviceInfo: () => deviceInfoRef.current,
            checkToken,
            audioLanguage,
            isBrazil,
            /* TEST override (2026-08-18): VITE_LEGO_PLAYER_CONFIG_OVERRIDE
               env JSON — deep-merged over the tier config in the solid side
               (legoPlayerConfig.ts). The localStorage key
               'legoPlayerConfigOverride' is ALSO read there at mount, so
               configs can be flipped on the TV without a rebuild. */
            legoConfigOverride: legoPlayerConfigOverride,
            getAudioTrackLabel: (track, index) => audioTrackLabelRef.current(track, index),
            getTextTrackLabel: (track, index) => textTrackLabelRef.current(track, index),
            /* Getter, not the object: the ref is re-assigned on every render, so
               the panel re-reads the labels when the locale changes instead of
               keeping the boot-time strings. */
            getTrackPanelLabels: () => trackPanelLabelsRef.current,
            /* Saved audio/subtitle choice (localStorage) — same key/shape as the
               old player, so a preference saved there restores here. */
            getTrackPreference,
            saveTrackPreference,
            /* Mini EPG (guide): channels/schedules/filters resolved in react-land.
               Stable identities (useMemo of useCallback([]) members), so it never
               remounts the player. Passed for VOD too — both the solid button and
               the hook's readyToFetch gate it to live, so it stays inert. */
            guide,
            /* Zapping from the guide replaces the route, which re-runs this
               effect and remounts the solid tree; read-and-clear so the fresh
               instance opens the guide again (the old mini EPG let the user keep
               changing channels without the panel closing). */
            startWithGuideOpen: guide.consumeReopenGuide(),
            /* Fatal failures go to the app's error screen (useLegoFatalError);
               stable identity, never remounts the player by itself. */
            onFatalError,
            /* Heartbeat + end of content; stable identity. */
            playbackSession,
          });
        }
        return undefined;
      })
      .catch((error) => {
        console.error(error);
        setImportError(String(error));
      });

    return () => {
      cancelled = true;
      dispose?.();
      playbackSession.stop();
    };
  }, [
    contentIdValue,
    isLiveValue,
    startEpochSeconds,
    isFetchingConfig,
    playerConfig,
    useLowLatencyStreams,
    isLowEndDevice,
    extendedSeekEnabled,
    audioLanguage,
    isBrazil,
    /* Stable identities (useMemo([]) in useLegoNextEpisode / useCallback([]) in
       useLegoTrackPreference / useMemo of useCallback([]) members in
       useLegoMiniEpg) — safe to include, would not remount the player. */
    getNextEpisode,
    onNextEpisode,
    getTrackPreference,
    saveTrackPreference,
    guide,
    onFatalError,
    getRetryResumeTime,
    playbackSession,
    /* Error screen on/off: clearing it (retry / connection back) remounts. */
    playerError,
  ]);

  return (
    /* Fullscreen POC surface: pin the shell to the viewport and clip any
       overflow so the player can never spawn document scrollbars (a 100vw
       child inside a narrower layout viewport would otherwise overflow
       horizontally when classic scrollbars shrink it). */
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden' }}>
      {/* The classic player's error screen (same component and messages as the
          Live/Vod routes): the lego only reports the failure. The container
          stays mounted so the remount after a reconnect reuses it. */}
      {playerError && (
        <LegoErrorScreen
          error={playerError}
          content={content}
          channel={channel}
          isLive={isLiveValue}
          channelId={contentIdValue}
          homeOnBack={homeOnBack}
          onRetry={retry}
        />
      )}
      <div
        ref={containerRef}
        style={{
          display: playerError ? 'none' : 'block',
          width: '100vw',
          height: '100vh',
          background: '#000',
          /* Positioning context for the lego surface overlay. */
          position: 'relative',
        }}
      />
      {importError && (
        <p style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: '#fff',
          'font-family': 'monospace',
        }}
        >
          {importError}
        </p>
      )}
    </div>
  );
}

export default memo(LegoPlayer);
